import React, {
  forwardRef,
  ForwardedRef,
  HTMLAttributes,
  ReactNode,
  useState,
  useLayoutEffect,
  ElementType,
  AriaRole,
} from 'react';
import clsx from 'clsx';
import { AriaButtonProps } from 'react-aria/useButton';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useObjectRef } from 'react-aria/useObjectRef';
import { AriaLabelingProps } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/list/list.module.css';
import { useListContext } from './ListContext';
import { ListItemContext } from './ListItemContext';
import { useListItemActionWrapper } from './useListItemActionWrapper';
import {
  SapphireStyleProps,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
  withTruthyZero as hasAllowedCustomSidePadding,
} from '../../utils';
import {
  AriaLabelingPropsKeys,
  FocusablePropsKeys,
  GlobalDomAttributes,
  PressEventsKeys,
} from '../../utils/types';

export interface ListItemProps
  extends Pick<AriaLabelingProps, AriaLabelingPropsKeys>,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * The html element to render as list item.
   * @default 'li'
   */
  elementType?: 'li' | 'div';

  role?: AriaRole;
  /**
   * The content of the list item. Use {@link ListItem.Content}. Wrap in a
   * {@link ListItem.Button} if list item should be interactive.
   */
  children: ReactNode;
}

const customPaddingStyleProps = (sidePadding: number | string) => ({
  paddingLeft:
    typeof sidePadding === 'string' ? sidePadding : `${sidePadding}px`,
  paddingRight:
    typeof sidePadding === 'string' ? sidePadding : `${sidePadding}px`,
});

const ListItem = forwardRef(function ListItem(
  props: ListItemProps,
  ref: ForwardedRef<HTMLElement>
) {
  useThemeCheck();

  const {
    children,
    role,
    elementType: CustomElement = 'li' as ElementType,
    ...otherProps
  } = props;

  const { sidePadding } = useListContext();
  const [isActionButtonHovered, setActionButtonHovered] = useState(false);
  const [hasContentWrapper, setHasContentWrapper] = useState(false);
  const forwardedRef = useObjectRef(ref);

  const { styleProps } = useSapphireStyleProps(otherProps);

  useLayoutEffect(() => {
    // `.${styles[...]}` fails in production build, because document.querySelector throws
    // on class selector that have "=" in their name
    setHasContentWrapper(
      !!forwardedRef.current?.querySelector(
        `[class*="${styles['sapphire-list__item--interactive']}"]`
      )
    );
  });

  return (
    <ListItemContext.Provider
      value={{ isActionButtonHovered, setActionButtonHovered }}
    >
      <CustomElement
        ref={forwardedRef}
        {...(role
          ? { role }
          : CustomElement !== 'li'
          ? { role: 'listitem' }
          : {})}
        className={clsx(styles['sapphire-list__item'], styleProps.className)}
        style={{
          ...(!hasContentWrapper && hasAllowedCustomSidePadding(sidePadding)
            ? customPaddingStyleProps(sidePadding)
            : {}),
          ...styleProps.style,
        }}
        {...filterDOMProps(otherProps, { global: true })}
      >
        {children}
      </CustomElement>
    </ListItemContext.Provider>
  );
});

// Exported only for PropsTable to work. they are not exposed from the library
export const ListItem_Content = ({
  left,
  contentLeft,
  right,
  contentRight,
}: {
  /**
   * The content displayed to the left on the list item.
   *
   * @deprecated This will be removed in the next major version. Use
   * `contentLeft` instead.
   */
  left?: React.ReactNode;
  /**
   * The content displayed to the left on the list item.
   */
  contentLeft?: React.ReactNode;
  /**
   * The content displayed to the right on the list item.
   *
   * @deprecated This will be removed in the next major version. Use
   * `contentRight` instead.
   */
  right?: React.ReactNode;
  /**
   * The content displayed to the right on the list item.
   */
  contentRight?: React.ReactNode;
}): JSX.Element => {
  const leftContent = contentLeft ?? left;
  const rightContent = contentRight ?? right;

  return (
    <>
      {leftContent && (
        <div className={clsx(styles['sapphire-list__item-content-left'])}>
          {leftContent}
        </div>
      )}
      {rightContent && (
        <div className={clsx(styles['sapphire-list__item-content-right'])}>
          {rightContent}
        </div>
      )}
    </>
  );
};

// Exported only for PropsTable to work. they are not exposed from the library
export const ListItem_Button = React.forwardRef(function ListItemButton(
  props: Pick<
    AriaButtonProps<'div'>,
    'children' | 'isDisabled' | FocusablePropsKeys | PressEventsKeys
  >,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { sidePadding } = useListContext();
  const { actionWrapperProps } = useListItemActionWrapper({
    isDisabled: props.isDisabled,
    style: hasAllowedCustomSidePadding(sidePadding)
      ? customPaddingStyleProps(sidePadding)
      : {},
  });
  const contentWrapperRef = useObjectRef<HTMLDivElement>(ref);

  const { buttonProps } = useButton(
    { ...props, elementType: 'div' },
    contentWrapperRef
  );

  const allProps = {
    ref: contentWrapperRef,
    ...mergeProps(buttonProps, actionWrapperProps),
  };

  return <div {...allProps}>{props.children}</div>;
});

// Exported only for PropsTable to work. they are not exposed from the library
export const ListItem_Link = ({
  children,
}: {
  /**
   * Render function which is expected to return a native or custom link element.
   * It's given the props to be applied on the rendered link element, to make
   * the list item be interactive.
   */
  children: (props: HTMLAttributes<HTMLAnchorElement>) => JSX.Element;
}): JSX.Element => {
  const { sidePadding } = useListContext();
  const { actionWrapperProps } = useListItemActionWrapper({
    style: hasAllowedCustomSidePadding(sidePadding)
      ? customPaddingStyleProps(sidePadding)
      : {},
  });
  return children(actionWrapperProps);
};

// Object.assign is used for the right typing.
// Based on this solution: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757
const _ListItem = Object.assign(ListItem, {
  Content: ListItem_Content,
  Button: ListItem_Button,
  Link: ListItem_Link,
});

export { _ListItem as ListItem };
